import React, { useState, useEffect } from 'react';
import './Modal.css'; 
import { useTranslation } from 'react-i18next';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faRotateLeft, faCrown, faRightFromBracket, faMicrophone, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import { useSharedState } from '../../hooks/SharedStateContext';
import { resetFirestoreDocId, getUserCustomBucket, saveUserTargetLanguage, fetchUserPreferences } from '../../utilities/firebase';
import ModalGeneric from './ModalGeneric';
import PronounSelect from './PronounSelect';
import { languageMap, syncLanguageChange } from '../../utilities/i18next';

// Target languages available for learning
// "中文 (Chinese (Mandarin))" reads badly, so a name that already carries its
// own qualifier is set off with a dash instead of a second pair of brackets.
const formatTargetLanguageOption = (lang) => (
  lang.name.includes('(')
    ? `${lang.nativeLabel} — ${lang.name}`
    : `${lang.nativeLabel} (${lang.name})`
);

const targetLanguageOptions = [
  { code: 'fr', name: 'French', nativeLabel: 'Français' },
  { code: 'en', name: 'English', nativeLabel: 'English' },
  { code: 'es', name: 'Spanish', nativeLabel: 'Español', isBeta: true },
  { code: 'sv', name: 'Swedish', nativeLabel: 'Svenska', isBeta: true },
  { code: 'zh', name: 'Chinese (Mandarin)', nativeLabel: '中文', isBeta: true },
  { code: 'it', name: 'Italian', nativeLabel: 'Italiano', isBeta: true },
];

function MoreOptionsModal({ isOpen, onClose, onNewConversation, onEndConversation, onResetAcknowledgement }) {
  const { t } = useTranslation();

  const { sharedState, setSharedState, updateLanguageVariant } = useSharedState();

  useEffect(() => {
    console.log('🇫🇷 Modal - sharedState.languageVariant:', sharedState?.languageVariant);
  }, [sharedState?.languageVariant, updateLanguageVariant]);

  // Fetch custom bucket when modal opens (cached in firebase.js after auth)
  const [customAgentId, setCustomAgentId] = useState(null);

  // Read-only label of the microphone the browser is currently using. We don't
  // manage device selection (most users have one mic); to change it users go
  // through their browser/system settings (see the hint below).
  const [microphoneLabel, setMicrophoneLabel] = useState('');
  useEffect(() => {
    if (isOpen) {
      const fetchCustomBucket = async () => {
        const bucket = await getUserCustomBucket();
        if (bucket?.elevenlabs_id) {
          setCustomAgentId(bucket.elevenlabs_id);
        }
      };
      fetchCustomBucket();
    }
  }, [isOpen]);

  // Detect the active microphone's label (device labels are only populated once
  // mic permission has been granted, which is the case during/after a conversation).
  useEffect(() => {
    if (!isOpen || !navigator.mediaDevices?.enumerateDevices) return;

    let cancelled = false;
    const detectMicrophone = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const inputs = devices.filter(device => device.kind === 'audioinput');
        // Prefer the browser/OS default entry; fall back to the first input.
        const active = inputs.find(device => device.deviceId === 'default') || inputs[0];
        if (cancelled) return;
        // Strip Chrome's "Default - " prefix for a cleaner name.
        setMicrophoneLabel((active?.label || '').replace(/^Default\s*-\s*/i, '').trim());
      } catch (error) {
        console.warn('Unable to read microphone label:', error);
        if (!cancelled) setMicrophoneLabel('');
      }
    };

    detectMicrophone();
    return () => { cancelled = true; };
  }, [isOpen]);

  // chrome:// pages can't be opened from a web page, so for Chromium browsers we
  // surface the settings path as copyable text rather than a (blocked) link.
  const isChromiumBrowser = typeof navigator !== 'undefined' && /Chrome|Chromium|Edg/.test(navigator.userAgent);

  const handleRealtimeToggleChange = () => {
    // If there's an active conversation, show confirmation
    if (sharedState.chatHistory && sharedState.chatHistory.length > 0) {
      setIsModeConfirmOpen(true);
      return;
    }

    // No active conversation, switch immediately
    confirmModeChange();
  };

  const confirmModeChange = () => {
    // Block realtime mode for organizations with childrenMode
    if (sharedState.childrenMode && !sharedState.isRealtime) {
      setIsModeConfirmOpen(false);
      return;
    }

    const newValue = !sharedState.isRealtime;

    // Reset conversation when switching modes to ensure clean state
    resetFirestoreDocId();

    setSharedState(prevState => ({
      ...prevState,
      isRealtime: newValue,
      // Reset conversation state for new mode
      chatHistory: [],
      recording: false,
      talking: false,
      loading: false,
      record: false,
      firstUserInput: false,
      showActivities: true,
      selectedActivity: null,
    }));
    localStorage.setItem('isRealtime', newValue);
    setIsModeConfirmOpen(false);
  };

  const cancelModeChange = () => {
    setIsModeConfirmOpen(false);
  };

  // Confirmation modal state for realtime variant change
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingVariant, setPendingVariant] = useState(null);
  const [uiVariant, setUiVariant] = useState(sharedState?.languageVariant || 'france');

  // Confirmation modal state for mode change
  const [isModeConfirmOpen, setIsModeConfirmOpen] = useState(false);

  // To Keep local UI value in sync with shared state
  React.useEffect(() => {
    setUiVariant(sharedState?.languageVariant || 'france');
  }, [sharedState?.languageVariant]);

  // When settings opens, show the canonical Firestore preference if it exists.
  // This prevents a missing/stale browser localStorage value from making the
  // variant selector appear to switch back to Quebec.
  React.useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    fetchUserPreferences()
      .then((prefs) => {
        if (cancelled || !prefs?.languageVariant) return;
        setUiVariant(prefs.languageVariant);
        setSharedState(prevState => ({
          ...prevState,
          languageVariant: prefs.languageVariant,
        }));
        localStorage.setItem('languageVariant', prefs.languageVariant);
      })
      .catch(error => {
        console.warn('Unable to load Firestore language variant preference:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, setSharedState]);

  const handleLanguageVariantChange = (event) => {
    const languageVariant = event.target.value;
    setUiVariant(languageVariant);

    // If realtime, ask confirmation before applying to avoid unexpected restart
    if (sharedState?.isRealtime) {
      setPendingVariant(languageVariant);
      setIsConfirmOpen(true);
      return;
    }

    if (typeof updateLanguageVariant === 'function') {
      updateLanguageVariant(languageVariant);
    } else {      
      setSharedState(prevState => ({
        ...prevState,
        languageVariant: languageVariant
      }));
      localStorage.setItem('languageVariant', languageVariant);
    }
  };

  const confirmVariantChange = () => {
    if (!pendingVariant) {
      setIsConfirmOpen(false);
      return;
    }
    if (typeof updateLanguageVariant === 'function') {
      updateLanguageVariant(pendingVariant);
    } else {
      setSharedState(prevState => ({
        ...prevState,
        languageVariant: pendingVariant
      }));
      localStorage.setItem('languageVariant', pendingVariant);
    }
    setIsConfirmOpen(false);
    if (typeof onClose === 'function') onClose();
  };

  const cancelVariantChange = () => {
    setPendingVariant(null);
    setIsConfirmOpen(false);
    // Revert UI to actual current state
    setUiVariant(sharedState?.languageVariant || 'france');
  };

  const handleCustomAgentToggle = () => {
    const newValue = !sharedState.useCustomAgent;
    setSharedState(prevState => ({
      ...prevState,
      useCustomAgent: newValue
    }));
    localStorage.setItem('useCustomAgent', newValue);
  };

  // "Audio model": 'default' (stock Gemini) or 'conversaflex' (our fine-tune).
  // The single model switch — it replaced the "Audio detection model" (sttModel)
  // selector, which chose a separate transcription model back when ours was
  // STT-only. Ours is the unified model now, so one turn, one choice.
  // Stored only once the user picks: an explicit choice is sent with every
  // streamed turn and overrides the server-side default in both directions
  // (see ConversationWT.jsx / cloud-run-ws-proxy/utils/llmStream.js).
  const handleAudioModelChange = (event) => {
    const newValue = event.target.value;
    setSharedState(prevState => ({
      ...prevState,
      audioModel: newValue
    }));
    localStorage.setItem('audioModel', newValue);
  };

  const handleTtsSpeedChange = (event) => {
    const newSpeed = parseFloat(event.target.value);
    setSharedState(prevState => ({
      ...prevState,
      ttsSpeed: newSpeed
    }));
    localStorage.setItem('ttsSpeed', newSpeed);
  };

  const handleHideAssistantTextChange = () => {
    const newValue = !sharedState.hideAssistantText;
    setSharedState(prevState => ({
      ...prevState,
      hideAssistantText: newValue
    }));
    localStorage.setItem('hideAssistantText', newValue);
  };

  const handleLanguageChange = (e) => {
    const newLanguageCode = e.target.value;
    syncLanguageChange(newLanguageCode);
    setSharedState(prevState => ({
      ...prevState,
      userLanguage: newLanguageCode
    }));
  };

  const handleTargetLanguageChange = (e) => {
    const newTargetLanguage = e.target.value;
    setSharedState(prevState => ({
      ...prevState,
      targetLanguage: newTargetLanguage
    }));
    localStorage.setItem('targetLanguage', newTargetLanguage);
    saveUserTargetLanguage(newTargetLanguage);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-content">
          <div className="modal-header"></div>
          <div className="modal-body">
            
            <div>
              <span>{t('setting_native_language')}</span>
              <select
                id="language-select"
                value={sharedState.userLanguage || 'en'}
                onChange={handleLanguageChange}
              >
                {Object.entries(languageMap).map(([name, code]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            {!sharedState.activityPageMode && (
              <div>
                <span>{t('setting_target_language')}</span>
                <select
                  value={sharedState.targetLanguage || 'fr'}
                  onChange={handleTargetLanguageChange}
                >
                  {targetLanguageOptions
                    .filter(lang => lang.code !== sharedState.userLanguage)
                    .map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {formatTargetLanguageOption(lang)}{lang.isBeta ? ' - Beta' : ''}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Only show language variant selector when learning French */}
            {!sharedState.activityPageMode && (sharedState.targetLanguage === 'fr' || sharedState.targetLanguage?.toLowerCase() === 'french') && (
              <div>
                <span>{t('language_variant')}</span>
                <select
                  value={uiVariant}
                  onChange={handleLanguageVariantChange}
                >
                  <option value="quebec">{t('language_quebec')}</option>
                  <option value="france">{t('language_france')}</option>
                </select>
              </div>
            )}

            <div className="settings-group conversation-group">
              <div className="settings-group-title">
                {t('setting_group_conversation', 'Conversation')}
              </div>

              {!sharedState.activityPageMode && (
                <div>
                  <span>{t('setting_realtime')}</span>
                  <label className="switch">
                    <input type="checkbox"
                      checked={Boolean(sharedState.isRealtime)}
                      onChange={handleRealtimeToggleChange}
                      disabled={sharedState.childrenMode} />
                    <span className="slider round"></span>
                  </label>
                </div>
              )}

              {!sharedState.activityPageMode && customAgentId && (
                <div>
                  <span>
                    {t('setting_use_custom_agent')}
                    <FontAwesomeIcon icon={faCrown} className="premium-badge" title={t('premium_feature')} />
                  </span>
                  <label className="switch">
                    <input type="checkbox"
                      checked={Boolean(sharedState.useCustomAgent)}
                      onChange={handleCustomAgentToggle} />
                    <span className="slider round"></span>
                  </label>
                </div>
              )}

              <div>
                <span>{t('setting_tts_speed')}</span>
                <select
                  value={sharedState.ttsSpeed || 1.0}
                  onChange={handleTtsSpeedChange}
                >
                  <option value="0.5">{t('tts_speed_050')}</option>
                  <option value="0.75">{t('tts_speed_075')}</option>
                  <option value="0.85">{t('tts_speed_085')}</option>
                  <option value="1">{t('tts_speed_100')}</option>
                </select>
              </div>

              {!sharedState.activityPageMode && !sharedState.isRealtime && (
                <div>
                  <span>{t('setting_audio_model')}</span>
                  <select
                    value={sharedState.audioModel || 'default'}
                    onChange={handleAudioModelChange}
                  >
                    <option value="default">{t('stt_model_default')}</option>
                    <option value="conversaflex">{t('stt_model_conversaflex')}</option>
                  </select>
                </div>
              )}

              <div>
                <span>{t('setting_hide_assistant_text')}</span>
                <label className="switch">
                  <input type="checkbox"
                    checked={Boolean(sharedState.hideAssistantText)}
                    onChange={handleHideAssistantTextChange} />
                  <span className="slider round"></span>
                </label>
              </div>

              <PronounSelect useLabel={false} />
            </div>

            <div className="settings-group microphone-group">
              <div className="settings-group-title">
                <FontAwesomeIcon icon={faMicrophone} /> {t('setting_microphone', 'Microphone')}
              </div>

              <div className="microphone-current-name">
                {microphoneLabel || t('setting_microphone_system_default', 'System default')}
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="info-icon"
                  data-tooltip-id="microphone-info-tooltip" />
              </div>
              <Tooltip
                id="microphone-info-tooltip"
                place="top"
                variant="dark"
                className="custom-tooltip microphone-info-tooltip"
                clickable
              >
                <div className="microphone-info-tooltip-content">
                  <span>
                    {isChromiumBrowser
                      ? t('setting_microphone_change_hint_chrome', 'To change your microphone, click the icon to the left of the address bar and open Site settings → Microphone, or open this address in a new tab:')
                      : t('setting_microphone_change_hint_generic', "To change your microphone, open your browser's site settings (the icon to the left of the address bar) → Microphone, or change the default input in your system sound settings.")}
                  </span>
                  {isChromiumBrowser && (
                    <code className="microphone-settings-path">chrome://settings/content/microphone</code>
                  )}
                </div>
              </Tooltip>

            </div>

            {!sharedState.activityPageMode && (
              <>
                <div className="button restart-button" onClick={onNewConversation}>
                    <FontAwesomeIcon icon={faRotateLeft} /> {t('new_conversation')}
                </div>
                {/* <div className="button reset-button" onClick={onEndConversation}>
                    <FontAwesomeIcon icon={faPhoneSlash} /> {t('end_conversation_action')}
                </div> */}
                {/* <div className="button reset-button" onClick={onResetAcknowledgement}>
                    <FontAwesomeIcon icon={faTrash} /> {t('reset_conversation')}
                </div> */}
                <div className="button restart-button" onClick={() => window.location.href = '/onboarding'}>
                    <FontAwesomeIcon icon={faRotateLeft} /> {t('restart_onboarding', 'Restart Onboarding')}
                </div>
                <div className="button restart-button" onClick={onResetAcknowledgement}>
                    <FontAwesomeIcon icon={faRightFromBracket} /> {t('sign_out')}
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button onClick={onClose} className="modal-button">Close</button>
          </div>
        </div>
      </div>
    </div>

  {/* Confirmation modal for realtime variant change */}
  <ModalGeneric
    isOpen={isConfirmOpen}
    onClose={cancelVariantChange}
    title={t('confirm_variant_change_title') || 'Change Language variant?'}
  >
    <p>
      {t('confirm_variant_change_body') ||
        'Changing the Language variant in realtime will start a new conversation. Do you want to continue?'}
    </p>
    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
      <button className="modal-button" onClick={cancelVariantChange}>{t('cancel') || 'Cancel'}</button>
      <button className="modal-button" onClick={confirmVariantChange}>{t('confirm_restart') || 'Yes, restart'}</button>
    </div>
  </ModalGeneric>

  {/* Confirmation modal for mode change */}
  <ModalGeneric
    isOpen={isModeConfirmOpen}
    onClose={cancelModeChange}
    title={t('confirm_mode_change_title') || 'Change conversation mode?'}
  >
    <p>
      {t('confirm_mode_change_body') ||
        'This will reset your current activity and start a new conversation. Do you want to continue?'}
    </p>
    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
      <button className="modal-button" onClick={cancelModeChange}>{t('cancel') || 'Cancel'}</button>
      <button className="modal-button" onClick={confirmModeChange}>{t('confirm_restart') || 'Yes, restart'}</button>
    </div>
  </ModalGeneric>
  </>
  );
}

export default MoreOptionsModal;
